import { inject, Injectable, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type SupportedLanguage = 'en' | 'de';

const LANGUAGE_STORAGE_KEY = 'talentpro.language';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private readonly translate = inject(TranslateService);

  readonly languages: Array<{ code: SupportedLanguage; label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'de', label: 'Deutsch' },
  ];

  readonly currentLanguage = signal<SupportedLanguage>('en');

  initialize(): void {
    const stored = this.getStoredLanguage();
    const browser = this.getBrowserLanguage();
    const initial = stored ?? browser ?? 'en';
    this.setLanguage(initial);
  }

  applyTenantDefault(code?: string | null): void {
    if (!code) return;
    if (this.getStoredLanguage()) return;
    const normalized = this.normalizeLanguage(code);
    if (!normalized) return;
    this.setLanguage(normalized);
  }

  setLanguage(code: SupportedLanguage): void {
    this.translate.setDefaultLang('en');
    this.translate.use(code);
    this.currentLanguage.set(code);
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      // Ignore storage errors (private mode).
    }
  }

  private getStoredLanguage(): SupportedLanguage | null {
    try {
      const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (stored === 'en' || stored === 'de') return stored;
      return null;
    } catch {
      return null;
    }
  }

  private getBrowserLanguage(): SupportedLanguage | null {
    const lang = navigator.language?.toLowerCase();
    if (!lang) return null;
    if (lang.startsWith('de')) return 'de';
    if (lang.startsWith('en')) return 'en';
    return null;
  }

  private normalizeLanguage(value: string): SupportedLanguage | null {
    const lower = value.toLowerCase();
    if (lower.startsWith('de')) return 'de';
    if (lower.startsWith('en')) return 'en';
    return null;
  }
}
